import { Inject, Injectable } from '@angular/core';
import { Subject, Observer, Observable, ReplaySubject } from 'rxjs';
import { WebSocketServiceConfig, WebSocketConnectionState, WebSocketServiceBase, WS_SERVICE_FACTORY, WSServiceFactory } from 'types';
import { PassportReaderServiceState, CertInfo } from './hw-service-model';
import { HWServiceConfig } from './hw-service-config';

enum ResponseType {
  None,
  CheckActivation,
  Activation,
  CheckCertDG3,
  SecurityBAC,
  SecurityEACbyICAO,
  SecurityEACbyGOST,
  EnumReaders,
  ConnectRead,
  HeaderRead,
  DataRead,
  PhotoRead,
  FingerprintsRead,
  ReadSODbyICAO,
  ReadSODbyGOST,
  VerifyPass,
  Encrypt,
  UpdateDG3Cert,
  CreateTerminalKey,
  CheckTerminalKeys,
  UploadCVCALinks,
  EnumCvCerts,
}

export enum ActivationState {
  Never = 'never',
  Process = 'process',
  CheckStatementMrzFailed = 'check-statement-mrz-failed',
  CheckStatementSelectionFailed = 'check-statement-selection-failed',
  CheckStatementActivationKeyFailed = 'check-statement-activation-key-failed',
  NotRequired = 'not-required',
  Activated = 'activated',
  Attempt = 'attempt',
  Error = 'error',
  ErrorCritical = 'error-critical',
  ErrorScanMRZ = 'error-scan-mrz',
}

export class DataGroup2 {
  result: boolean;
  id: string;
  data: any;
}

export class DataGroup<T = any> extends DataGroup2 {
  override data: T = undefined as T;
}

export class PassportStatusSOD {
  icao_bac: boolean;
  icao_eac: boolean;
  gost_bac: boolean;
  gost_eac: boolean;
}

export interface Activation extends Omit<DataGroup, 'data' | 'id'> {}

export interface ActivationError {
  message: string;
  tries_left: string;
}

export interface CheckActivation extends Activation {
  command: string;
  activated: boolean;
}

export interface TerminalKeyRequestResponse extends Omit<DataGroup, 'data' | 'id'> {
  'CHR': string;
  'key-id': string;
  'req-id': string;
  'protocol-name': string;
  'protocol-oid': string;
  'request': string;
}

@Injectable()
export class PassportReaderService {
  private serviceState: PassportReaderServiceState = PassportReaderServiceState.Closed;
  private connectionStateSubject: Subject<PassportReaderServiceState>;
  private isPassportActivated: ReplaySubject<ActivationState>;
  private socket: WebSocketServiceBase;
  private responseType: ResponseType = ResponseType.None;
  private SecurityBAC: Observer<string> | null = null;
  private SecurityEACbyICAO: Observer<string> | null = null;
  private SecurityEACbyGOST: Observer<string> | null = null;
  private enumReadersObserver: Observer<Map<number, string>> | null = null;
  private connector: Observer<string> | null = null;
  private dataECPbyICAO: Observer<DataGroup2 | number> | null = null;
  private dataECPbyGOST: Observer<DataGroup2 | number> | null = null;
  private dgCOM: Observer<DataGroup2 | number> | null = null;
  private dg1: Observer<DataGroup2 | number> | null = null;
  private dg2: Observer<DataGroup2 | number> | null = null;
  private dg3: Observer<DataGroup2 | number> | null = null;
  private verifyPass: Observer<PassportStatusSOD | number> | null = null;
  private encryptResultObserver: Observer<string> | null = null;
  private check_dg3_cert: Observer<CertInfo> | null = null;
  private update_dg3_cert: Observer<boolean> | null = null;
  private createTerminalKeyObserver: Observer<any> | null = null;
  private checkTerminalKeysObserver: Observer<boolean> | null = null;
  private checkPassportActivationObserver: Observer<CheckActivation> | null = null;
  private activationObserver: Observer<Activation> | null = null;
  private uploadCVCALinksObserver: Observer<string> | null = null;
  private enumCvCertsObserver: Observer<any[]> | null = null;

  public constructor(config: HWServiceConfig, @Inject(WS_SERVICE_FACTORY) createWebSocket: WSServiceFactory) {
    this.connectionStateSubject = new Subject<PassportReaderServiceState>();
    this.isPassportActivated = new ReplaySubject<ActivationState>();
    this.socket = createWebSocket(new WebSocketServiceConfig(config.getHWServiceUrl(), config.getPassportReaderServiceProtocol()));
    this.socket.messages$.subscribe(
      (message) => {
        this.onSocketMessageReceived(message);
      },
      (error) => {
        this.onSocketMessageReceived({ result: false, error: error });
      },
    );
    this.socket.connectionState$.subscribe(
      (state) => {
        this.onSocketConnectionStateChanged(state);
      },
      (error) => {
        this.connectionStateSubject.error(error);
      },
    );
  }

  public setActivationState(state: ActivationState): void {
    this.isPassportActivated.next(state);
  }

  //чтобы состояние сервиса могли слушать другие компоненты, при необходимости
  public get connectionState$(): Observable<PassportReaderServiceState> {
    return this.connectionStateSubject.pipe();
  }

  public get passportActivationState$(): Observable<ActivationState> {
    return this.isPassportActivated;
  }

  // подключение
  public connect(): void {
    if (this.canConnect()) {
      this.socket.connect();
    } else {
      this.notifyOperationDenied('CONNECT');
    }
  }

  //отключение
  public disconnect(): void {
    if (this.canDisconnect()) {
      this.resetResponseSettings();
      this.socket.disconnect();
    } else {
      this.notifyOperationDenied('DISCONNECT');
    }
  }

  private get isOpened$(): boolean {
    return this.canDisconnect();
  }

  private canPerformOperation(): boolean {
    return this.isOpened$ && this.responseType === ResponseType.None;
  }

  public GetEncryptionCert(): Observable<CertInfo> {
    return new Observable((observer) => {
      if (this.canPerformOperation()) {
        this.socket.send({ command: 'check-dg3-cert' });
        this.responseType = ResponseType.CheckCertDG3;
        this.check_dg3_cert = observer;
      } else {
        this.notifyOperationDenied('CHECK_DG3_CERT', observer);
      }
    });
  }

  public UpdateEncryptionCert(certPemBase64: string): Observable<boolean> {
    return new Observable((observer) => {
      if (this.canPerformOperation()) {
        this.socket.send({ command: 'update-dg3-cert', cert: certPemBase64 });
        this.responseType = ResponseType.UpdateDG3Cert;
        this.update_dg3_cert = observer;
      } else {
        this.notifyOperationDenied('UPDATE_DG3_CERT', observer);
      }
    });
  }

  public EnumPassportReaders(): Observable<Map<number, string>> {
    return new Observable((o) => {
      this.socket.send({ command: 'list-readers' });
      this.responseType = ResponseType.EnumReaders;
      this.enumReadersObserver = o;
    });
  }

  // коннектимся
  public ConnectPassportReader(id: number): Observable<string> {
    return new Observable((o) => {
      this.socket.send({ 'command': 'connect', 'reader-id': id });
      this.responseType = ResponseType.ConnectRead;
      this.connector = o;
    });
  }

  // Проверка активации
  public CheckPassportActivation(id: number): Observable<CheckActivation> {
    const data = new Observable<CheckActivation>((response) => {
      this.socket.send({ 'command': 'is-activated', 'reader-id': id });
      this.responseType = ResponseType.CheckActivation;
      this.checkPassportActivationObserver = response;
    });
    return data;
  }

  // Активация пасспрта по коду
  public activatePassportByCode(id: number, code: string): Observable<Activation> {
    const data = new Observable<Activation>((response) => {
      this.socket.send({ 'command': 'activate-by-code', 'code': code, 'reader-id': id });
      this.responseType = ResponseType.Activation;
      this.activationObserver = response;
    });
    return data;
  }

  // проходим бак-проверку
  public GetCheckBAC(mrz2: string): Observable<string> {
    return new Observable((o) => {
      this.socket.send({ command: 'perform-bac', mrz_second_line: mrz2 });
      this.responseType = ResponseType.SecurityBAC;
      this.SecurityBAC = o;
    });
  }
  public GetCheckEACbyICAO(): Observable<string> {
    return new Observable((o) => {
      this.socket.send({ command: 'perform-eac', gost: false });
      this.responseType = ResponseType.SecurityEACbyICAO;
      this.SecurityEACbyICAO = o;
    });
  }
  public GetCheckEACbyGOST(): Observable<string> {
    return new Observable((o) => {
      this.socket.send({ command: 'perform-eac', gost: true });
      this.responseType = ResponseType.SecurityEACbyGOST;
      this.SecurityEACbyGOST = o;
    });
  }
  // получаем данные о сертификатах
  public EnumCvCerts(role: string): Observable<any[]> {
    const data = new Observable<any[]>((o) => {
      this.socket.send({ command: 'list-cvcerts', role: role });
      this.responseType = ResponseType.EnumCvCerts;
      this.enumCvCertsObserver = o;
    });
    return data;
  }
  // получаем данные о ЭЦП
  public CheckTerminalKeys(): Observable<boolean> {
    const data = new Observable<boolean>((o) => {
      this.socket.send({ command: 'list-terminal-keys', requests: 'false' });
      this.responseType = ResponseType.CheckTerminalKeys;
      this.checkTerminalKeysObserver = o;
    });
    return data;
  }
  // получаем данные о ЭЦП
  public GetPassSODbyICAO(): Observable<DataGroup2 | number> {
    const data = new Observable<DataGroup2 | number>((o) => {
      this.socket.send({ command: 'read-data-group', id: 'SOD', progress: true });
      this.responseType = ResponseType.ReadSODbyICAO;
      this.dataECPbyICAO = o;
    });
    return data;
  }
  // получаем данные о ГОСТ-ЭЦП
  public GetPassSODbyGost(): Observable<DataGroup2 | number> {
    const data = new Observable<DataGroup2 | number>((o) => {
      this.socket.send({ command: 'read-data-group', id: 'GOST_SOD', progress: true });
      this.responseType = ResponseType.ReadSODbyGOST;
      this.dataECPbyGOST = o;
    });
    return data;
  }

  // верификация
  public VerifyPass(): Observable<PassportStatusSOD | number> {
    const data = new Observable<PassportStatusSOD | number>((o) => {
      this.socket.send({ command: 'read-passport', progress: true });
      this.responseType = ResponseType.VerifyPass;
      this.verifyPass = o;
    });
    return data;
  }

  // получаем данные о Паспорте
  public GetPassHeader(): Observable<DataGroup2 | number> {
    const data = new Observable<DataGroup2 | number>((o) => {
      this.socket.send({ command: 'read-data-group', id: 'COM', progress: false, parse: true });
      this.responseType = ResponseType.HeaderRead;
      this.dgCOM = o;
    });
    return data;
  }
  // получаем данные о Паспорте
  public GetPassData(): Observable<DataGroup2 | number> {
    const data = new Observable<DataGroup2 | number>((o) => {
      this.socket.send({ command: 'read-data-group', id: 'DG1', progress: true, parse: true });
      this.responseType = ResponseType.DataRead;
      this.dg1 = o;
    });
    return data;
  }
  // получаем картинку с чипа-паспорта
  public GetPassPhoto(): Observable<DataGroup2 | number> {
    const data = new Observable<DataGroup2 | number>((o) => {
      this.socket.send({ command: 'read-data-group', id: 'DG2', progress: true, parse: true });
      this.responseType = ResponseType.PhotoRead;
      this.dg2 = o;
    });
    return data;
  }
  // получаем отпечатки с чипа-паспорта
  public GetPassFingerprints(): Observable<DataGroup2 | number> {
    const data = new Observable<DataGroup2 | number>((o) => {
      this.socket.send({ command: 'read-data-group', id: 'DG3', progress: true, parse: true });
      this.responseType = ResponseType.FingerprintsRead;
      this.dg3 = o;
    });
    return data;
  }

  public EncryptFingers(fingers: any[]): Observable<any> {
    return new Observable((o) => {
      if (fingers.length == 2) {
        this.socket.send({
          command: 'create-dg3',
          encrypt: true,
          fingers: [
            { id: fingers[0][0], width: 320, height: 480, data: fingers[0][1] },
            { id: fingers[1][0], width: 320, height: 480, data: fingers[1][1] },
          ],
        });
      } else {
        this.socket.send({
          command: 'create-dg3',
          encrypt: true,
          fingers: [{ id: fingers[0][0], width: 320, height: 480, data: fingers[0][1] }],
        });
      }
      this.responseType = ResponseType.Encrypt;
      this.encryptResultObserver = o;
    });
  }

  public CreateTerminalKey(algorithm: 'GOST' | 'ECDSA'): Observable<TerminalKeyRequestResponse> {
    const data = new Observable<TerminalKeyRequestResponse>((o) => {
      this.socket.send({ command: 'create-terminal-key', algorithm: algorithm });
      this.responseType = ResponseType.CreateTerminalKey;
      this.createTerminalKeyObserver = o;
    });

    return data;
  }

  public UploadCVCALinks(): Observable<string> {
    const data = new Observable<string>((o) => {
      this.socket.send({ command: 'upload-cvca-links' });
      this.responseType = ResponseType.UploadCVCALinks;
      this.uploadCVCALinksObserver = o;
    });

    return data;
  }

  //можем ли подключиться (всё ок, когда состояние сервиса закрыто)
  private canConnect(): boolean {
    return this.serviceState === PassportReaderServiceState.Closed;
  }

  //можем ли отключиться (всё ок, когда состояние сервиса НЕ закрыто)
  private canDisconnect(): boolean {
    return this.serviceState !== PassportReaderServiceState.Closed;
  }

  //установить состояние сервиса, зависимое от ответа сокета
  private setServiceState(state: PassportReaderServiceState): void {
    if (this.serviceState !== state) {
      this.serviceState = state;
      this.connectionStateSubject.next(state);
    }
  }

  //собственно тут мы получаем ответ вэбсокета для предыдущей функции
  private onSocketConnectionStateChanged(state: WebSocketConnectionState): void {
    switch (state) {
      case WebSocketConnectionState.Opened:
        this.setServiceState(PassportReaderServiceState.Ready);
        break;
      case WebSocketConnectionState.Connecting:
      case WebSocketConnectionState.Disconnecting:
      case WebSocketConnectionState.Closed:
        this.setServiceState(PassportReaderServiceState.Closed);
        break;
    }
  }

  // получаем сообщение от вэбсокета
  private onSocketMessageReceived(message: any): void {
    switch (this.responseType) {
      case ResponseType.CheckActivation:
        if (this.checkPassportActivationObserver) this.standartSocketReturnHandler(message, this.checkPassportActivationObserver);
        break;
      case ResponseType.Activation:
        if (this.activationObserver) this.activation(message, this.activationObserver);
        break;
      case ResponseType.CheckCertDG3:
        if (this.check_dg3_cert) this.standartSocketReturnHandler(message, this.check_dg3_cert);
        break;
      case ResponseType.UpdateDG3Cert:
        if (this.update_dg3_cert) this.updateDg3ReturnHandler(message, this.update_dg3_cert);
        break;
      case ResponseType.EnumReaders:
        if (this.enumReadersObserver) this.enumHandler(message, this.enumReadersObserver);
        break;
      case ResponseType.ConnectRead:
        if (this.connector) this.standartSocketReturnHandler(message, this.connector);
        break;
      case ResponseType.SecurityBAC:
        if (this.SecurityBAC) this.standartSocketReturnHandler(message, this.SecurityBAC);
        break;
      case ResponseType.SecurityEACbyICAO:
        if (this.SecurityEACbyICAO) this.standartSocketReturnHandler(message, this.SecurityEACbyICAO);
        break;
      case ResponseType.SecurityEACbyGOST:
        if (this.SecurityEACbyGOST) this.standartSocketReturnHandler(message, this.SecurityEACbyGOST);
        break;
      case ResponseType.HeaderRead:
        if (this.dgCOM) this.readingData(message, this.dgCOM);
        break;
      case ResponseType.DataRead:
        if (this.dg1) this.readingData(message, this.dg1);
        break;
      case ResponseType.PhotoRead:
        if (this.dg2) this.readingData(message, this.dg2);
        break;
      case ResponseType.FingerprintsRead:
        if (this.dg3) this.readingData(message, this.dg3);
        break;
      case ResponseType.ReadSODbyICAO:
        if (this.dataECPbyICAO) this.readingData(message, this.dataECPbyICAO);
        break;
      case ResponseType.ReadSODbyGOST:
        if (this.dataECPbyGOST) this.readingData(message, this.dataECPbyGOST);
        break;
      case ResponseType.VerifyPass:
        if (this.verifyPass) this.verification(message, this.verifyPass);
        break;
      case ResponseType.Encrypt:
        if (this.encryptResultObserver) this.standartSocketReturnHandler(message, this.encryptResultObserver);
        break;
      case ResponseType.CreateTerminalKey:
        if (this.createTerminalKeyObserver) this.standartSocketReturnHandler(message, this.createTerminalKeyObserver);
        break;
      case ResponseType.CheckTerminalKeys:
        if (this.checkTerminalKeysObserver) this.checkTerminalKeysHandler(message, this.checkTerminalKeysObserver);
        break;
      case ResponseType.UploadCVCALinks:
        if (this.uploadCVCALinksObserver) this.standartSocketReturnHandler(message, this.uploadCVCALinksObserver);
        break;
      case ResponseType.EnumCvCerts:
        if (this.enumCvCertsObserver) this.enumCvCertsHandler(message, this.enumCvCertsObserver);
        break;
    }
  }

  private enumCvCertsHandler(message: any, O: Observer<any[]>) {
    this.resetResponseSettings();
    if (message.result) {
      O.next(message.certificates);
      O.complete();
    } else {
      O.error(message.error);
    }
  }

  private checkTerminalKeysHandler(message: any, O: Observer<boolean>) {
    this.resetResponseSettings();
    if (message.result) {
      O.next(message.keys.length ? true : false);
      O.complete();
    } else {
      O.error(message.error);
    }
  }

  private updateDg3ReturnHandler(message: any, O: Observer<any>) {
    this.resetResponseSettings();
    if (message.result) {
      O.next(true);
      O.complete();
    } else {
      O.next(false);
      O.complete();
    }
  }

  private enumHandler(message: any, O: Observer<Map<number, string>>) {
    this.resetResponseSettings();
    if (message.result) {
      const v = new Map<number, string>();
      for (const reader of message.readers) {
        v.set(reader.id, reader.name);
      }
      O.next(v);
      O.complete();
    } else {
      O.error(message.error);
    }
  }

  private standartSocketReturnHandler(message: any, O: Observer<any>) {
    this.resetResponseSettings();
    if (message.result) {
      O.next(message);
      O.complete();
    } else {
      O.error(message.error);
    }
  }

  private activation(message: any, O: Observer<any>) {
    this.resetResponseSettings();
    if (message.result) {
      O.next(message);
      O.complete();
    } else {
      const error: ActivationError = {
        message: message.error,
        tries_left: message['tries-left'],
      };
      O.error(error);
    }
  }

  private readingData(message: any, O: Observer<number | DataGroup2>) {
    if (!!message.error) {
      this.resetResponseSettings();
      O.error(message.error);
    } else if (!!message.progress) O.next(message.progress);
    else if (!!message.result) {
      this.resetResponseSettings();
      O.next(message);
      O.complete();
    }
  }

  private verification(message: any, O: Observer<number | PassportStatusSOD>) {
    if (!!message.error) {
      this.resetResponseSettings();
      O.error(message.error);
    } else if (!!message.progress) O.next(message.progress);
    else if (!!message.result) {
      this.resetResponseSettings();
      const status = new PassportStatusSOD();
      status.icao_bac = message['icao-bac'] === 'OK';
      status.icao_eac = message['icao-eac'] === 'OK';
      status.gost_bac = message['gost-bac'] === 'OK';
      status.gost_eac = message['gost-eac'] === 'OK';
      O.next(status);
      O.complete();
    }
  }

  // обнуляем ответы при выключении, отключении сокета
  private resetResponseSettings(): void {
    this.responseType = ResponseType.None;
  }
  // протоколирование данных для лога и прочего
  private notifyOperationDenied<T>(operation: string, observer: Observer<T> | null = null): void {
    var message =
      'Cannot perform operation "' +
      operation +
      '". ' +
      'Processing state: ' +
      PassportReaderServiceState[this.serviceState] +
      '. ' +
      'Expected response: ' +
      ResponseType[this.responseType] +
      '.';

    if (observer) {
      observer.error(message);
    } else {
      this.connectionStateSubject.error(message);
    }
  }
}
